import { useQuery } from '@tanstack/react-query'
import { Gift, Building2, Users2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import {
  Alert,
  Card,
  CardHeader,
  PageHeader,
  SkeletonRows,
  mutedTextClass,
} from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { messageFor } from '@/lib/errors'
import { queryKeys } from '@/lib/queryClient'
import { adminApi } from './api'
import type { AdminCount } from './types'

/**
 * `/admin` — what this deployment currently is.
 *
 * Four numbers and two breakdowns, chosen to answer the questions that actually
 * get asked: how many workspaces exist, how many people, how many are paying,
 * and how many are on a plan somebody gave them for free. That last figure is
 * the one this console can move, so it is a tile rather than a row in a table.
 */
export default function AdminOverviewPage() {
  const overview = useQuery({
    queryKey: queryKeys.adminOverview,
    queryFn: ({ signal }) => adminApi.overview({ signal }),
  })

  if (overview.isPending) {
    return (
      <>
        <PageHeader title="Overview" description="Every workspace and account on this deployment." />
        <SkeletonRows rows={5} />
      </>
    )
  }

  if (overview.isError) {
    return (
      <>
        <PageHeader title="Overview" />
        <Alert tone="danger">{messageFor(overview.error)}</Alert>
      </>
    )
  }

  const data = overview.data
  const paying = data.subscriptions
    .filter((row) => row.key === 'active' || row.key === 'trialing')
    .reduce((sum, row) => sum + row.count, 0)

  return (
    <>
      <PageHeader
        title="Overview"
        description="Every workspace and account on this deployment, across all tenants."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Workspaces" value={data.workspaces.total} icon={Building2}>
          {data.workspaces.team} team · {data.workspaces.personal} personal
        </Stat>
        <Stat label="People" value={data.users.total} icon={Users2}>
          {data.users.admins} platform {data.users.admins === 1 ? 'admin' : 'admins'}
        </Stat>
        <Stat label="Paying" value={paying} icon={Gift}>
          Active or trialing subscriptions
        </Stat>
        <Stat label="Comped" value={data.comped} icon={Gift} accent>
          <Link to="/admin/workspaces" className="rounded-sm underline underline-offset-2">
            On a manual grant
          </Link>
        </Stat>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Breakdown
          title="Workspaces by plan"
          description="Every tier, including the ones nobody is on."
          rows={data.plans}
          total={data.workspaces.total}
          empty="No workspaces yet."
        />
        <Breakdown
          title="Subscriptions by status"
          description="What Stripe currently says about each billing account."
          rows={data.subscriptions}
          total={data.subscriptions.reduce((sum, row) => sum + row.count, 0)}
          empty="No workspace has a Stripe subscription yet."
        />
      </div>
    </>
  )
}

function Stat({
  label,
  value,
  icon: Icon,
  accent,
  children,
}: {
  label: string
  value: number
  icon: typeof Building2
  accent?: boolean
  children?: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'rounded-card border bg-white px-5 py-4',
        accent ? 'border-brand-200 bg-brand-50' : 'border-ink-200',
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className={cn('size-4', accent ? 'text-brand-600' : 'text-ink-400')} aria-hidden="true" />
        <p className="text-xs font-medium tracking-[0.08em] text-ink-500 uppercase">{label}</p>
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums">{value.toLocaleString()}</p>
      <p className={cn('mt-1 text-xs', accent ? 'text-brand-800' : mutedTextClass)}>{children}</p>
    </div>
  )
}

/**
 * A breakdown as a labelled bar list rather than a pie: these are parts of one
 * whole read at a glance, and the comparison that matters is "which row is
 * biggest", which a shared baseline answers and angles do not.
 */
function Breakdown({
  title,
  description,
  rows,
  total,
  empty,
}: {
  title: string
  description: string
  rows: AdminCount[]
  total: number
  empty: string
}) {
  const max = Math.max(1, ...rows.map((row) => row.count))

  return (
    <Card>
      <CardHeader title={title} description={description} />
      {rows.length === 0 ? (
        <p className={cn('px-5 py-6 text-sm', mutedTextClass)}>{empty}</p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center gap-4 px-5 py-2.5">
              <span className="w-28 shrink-0 truncate text-sm font-medium">{row.label}</span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100">
                <span
                  className="block h-full rounded-full bg-brand-500"
                  style={{ width: `${row.count === 0 ? 0 : Math.max(4, (row.count / max) * 100)}%` }}
                />
              </span>
              <span className="w-16 shrink-0 text-right text-sm tabular-nums">
                {row.count.toLocaleString()}
              </span>
              <span className={cn('w-12 shrink-0 text-right text-xs tabular-nums', mutedTextClass)}>
                {total === 0 ? '—' : `${Math.round((row.count / total) * 100)}%`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
